import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { TaxonomyFieldIcon } from '@modules/common/components/TaxonomyFieldIcon';
import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { Category } from '@modules/common/types';
import { LabelUpdateInProgressIcon } from '@modules/taxonomyV2/components/LabelUpdateInProgressIcon';
import { getInfoMessageForLabelState } from '@modules/taxonomyV2/helpers';
import { ChevronRight } from '@mui/icons-material';
import { Stack, Tooltip } from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';

interface TaxonomyNodePathItem {
  id: string;
  displayName: string;
  type: LabelType;
  isVisible: boolean;
  category?: Category;
  isLabelUpdateInProgress: boolean;
}

interface TaxonomyNodePathLabelProps {
  pathNodes: TaxonomyNodePathItem[];
}

export const TaxonomyNodePathLabel = ({
  pathNodes,
}: TaxonomyNodePathLabelProps) => {
  const theme = useTheme();

  const lastNode = pathNodes[pathNodes.length - 1];

  const typographyVariant =
    lastNode.type === LabelType.Reason ? 'caption' : 'subtitle2';

  const renderPathNode = (nodePathItem: TaxonomyNodePathItem) => {
    const {
      displayName,
      type: labelType,
      category: labelCategory,
      isVisible,
      isLabelUpdateInProgress,
    } = nodePathItem;

    const isLast = nodePathItem.id === lastNode.id;

    const shouldShowInvisibleLabel = !isVisible && isLast;
    const shouldShowUpdateInProgressIcon = isLabelUpdateInProgress && isLast;

    return (
      <Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
        <Stack>
          <TaxonomyFieldIcon
            type={labelType}
            sx={{
              fontSize: 'small',
            }}
          />
        </Stack>
        {labelCategory && (
          <Stack>
            <CategoryIcon
              category={labelCategory}
              size={13}
              fill={
                shouldShowInvisibleLabel
                  ? theme.palette.text.disabled
                  : undefined
              }
            />
          </Stack>
        )}
        <Stack minWidth={0}>
          <Tooltip
            arrow
            title={
              shouldShowInvisibleLabel
                ? getInfoMessageForLabelState({
                    labelState: 'invisible',
                    labelType,
                  })
                : null
            }
            placement="right"
          >
            <TypographyWithEllipsis
              variant={typographyVariant}
              color={
                shouldShowInvisibleLabel ? 'text.disabled' : 'text.primary'
              }
            >
              {displayName}
            </TypographyWithEllipsis>
          </Tooltip>
        </Stack>
        {shouldShowUpdateInProgressIcon && (
          <Stack>
            <LabelUpdateInProgressIcon size={15} labelType={labelType} />
          </Stack>
        )}
      </Stack>
    );
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.2} minWidth={0}>
      {pathNodes.map((nodePathItem, index) => {
        const isLast = index === pathNodes.length - 1;
        return (
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={0.2}
            key={nodePathItem.id}
            minWidth={isLast ? 0 : undefined} // Allow ellipsis for the last node
          >
            {renderPathNode(nodePathItem)}
            {!isLast && <ChevronRight fontSize="small" color="primary" />}
          </Stack>
        );
      })}
    </Stack>
  );
};
